import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  DollarRate,
  Inventory,
  MarketPriceInfo,
  Nullable,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';

export const listInventory = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithMeta<Inventory>>(gql`
    query fullInventory {
      data: fullInventory {
        data {
          id
          konamiId
          quantity
          language
          rarity
          state
          salable

          box: warehouse {
            id
            name
          }

          cardSet {
            id
            code
            rarity
            image

            lastCardMarket
            lastCardTrader
            lastTcgPlayer

            card {
              name
            }

            set {
              konamiName
            }
          }
        }
        meta {
          total
          perPage
          lastPage
        }
      }
    }
  `);

export const deleteInventoryItem = async (
  user: Nullable<UserWithToken>,
  variables: { itemId: number; warehouseId: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Inventory>>(
    gql`
      mutation ($warehouseId: Int!, $itemId: Int!) {
        data: deleteInventoryItem(warehouseId: $warehouseId, itemId: $itemId) {
          id
        }
      }
    `,
    variables
  );

export const moveInventoryItem = async (
  user: Nullable<UserWithToken>,
  variables: { itemId: number; warehouseId: number; quantity: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Inventory>>(
    gql`
      mutation ($itemId: Int!, $warehouseId: Int!, $quantity: Int!) {
        data: moveInventoryItem(
          quantity: $quantity
          warehouseId: $warehouseId
          itemId: $itemId
        ) {
          id
        }
      }
    `,
    variables
  );

export const getInventoryItem = async (
  user: Nullable<UserWithToken>,
  variables: { itemId: number; warehouseId: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Inventory>>(
    gql`
      query ($itemId: Int!, $warehouseId: Int!) {
        data: inventory(warehouseId: $warehouseId, itemId: $itemId) {
          id
          konamiId
          quantity
          language
          rarity
          state

          cardSet {
            code

            card {
              name
            }
          }
        }
      }
    `,
    variables
  );

export const updateInventoryItem = async (
  user: Nullable<UserWithToken>,
  variables: { itemId: number; language: string; state: string }
) =>
  getGraphQLQueryClient(user).request<WithData<Inventory>>(
    gql`
      mutation ($itemId: Int!, $language: String!, $state: String!) {
        data: updateInventoryItem(
          itemId: $itemId
          language: $language
          state: $state
        ) {
          id
        }
      }
    `,
    variables
  );

export const getDollarRate = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<DollarRate>>(gql`
    query dollarRate {
      data: dollarRate {
        blue
        oficial
        euroBlue
        euroOficial
        fetchedAt
      }
    }
  `);

export const getMarketPriceInfo = async (
  user: Nullable<UserWithToken>,
  variables: { cardSetId: number; language?: string | null; rarity?: string }
) =>
  getGraphQLQueryClient(user).request<WithData<MarketPriceInfo>>(
    gql`
      query marketPriceInfo($cardSetId: Int!, $language: String, $rarity: String) {
        data: marketPriceInfo(cardSetId: $cardSetId, language: $language, rarity: $rarity) {
          min
          max
          average
          count
        }
      }
    `,
    variables
  );

export const markSalable = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Inventory>>(
    gql`
      mutation ($id: Int!) {
        data: markInventorySalable(id: $id) {
          id
          salable
        }
      }
    `,
    variables
  );

export const markNoSalable = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<Inventory>>(
    gql`
      mutation ($id: Int!) {
        data: markInventoryNotSalable(id: $id) {
          id
          salable
        }
      }
    `,
    variables
  );
